export type RegenerateStatus = "allowed" | "cooldown" | "queued" | "generating";

export type StrategyDisplayStateInput = {
  selectedClusterId: string | null;
  selectedClusterVisibilityScore: number | null;
  hasCoverageReady: boolean;
  hasCoverageSnapshot: boolean;
  hasCompleteCoverage: boolean;
  hasPlanTasks: boolean;
  strategyTasksReady: boolean;
  selectedClusterHasGeneratedBoost: boolean;
  isSelectedClusterGenerating: boolean;
  isRegenerateRunning: boolean;
  isSelectedClusterStrategyLoading: boolean;
  isPersistedOnboardingStrategyLoading: boolean;
  isInitialStrategyDataFetchPending: boolean;
  hasEvidenceData: boolean;
  evidenceWaitExpired: boolean;
  planInitialLoading: boolean;
  regenerateStatus: RegenerateStatus;
};

export type StrategyDisplayState = {
  isSelectedClusterFullyVisible: boolean;
  hasGeneratedStrategyForSelectedCluster: boolean;
  hasCompletedStrategyForSelectedCluster: boolean;
  shouldShowOnboardingInitialLoader: boolean;
  shouldShowGenerateOverlay: boolean;
  isPlanLoading: boolean;
  shouldShowInitialGenerationLoader: boolean;
  shouldShowRefreshLoader: boolean;
  shouldShowStrategyProcessingState: boolean;
};

export function resolveStrategyDisplayState(
  input: StrategyDisplayStateInput,
): StrategyDisplayState {
  const {
    selectedClusterId,
    selectedClusterVisibilityScore,
    hasCoverageReady,
    hasCoverageSnapshot,
    hasCompleteCoverage,
    hasPlanTasks,
    strategyTasksReady,
    selectedClusterHasGeneratedBoost,
    isSelectedClusterGenerating,
    isRegenerateRunning,
    isSelectedClusterStrategyLoading,
    isPersistedOnboardingStrategyLoading,
    isInitialStrategyDataFetchPending,
    hasEvidenceData,
    evidenceWaitExpired,
    planInitialLoading,
    regenerateStatus,
  } = input;

  const isSelectedClusterFullyVisible = Boolean(
    selectedClusterVisibilityScore !== null &&
      selectedClusterVisibilityScore >= 100 &&
      (hasCoverageReady || hasCompleteCoverage),
  );

  const hasGeneratedStrategyForSelectedCluster = Boolean(
    hasPlanTasks || selectedClusterHasGeneratedBoost,
  );

  const hasCompletedStrategyForSelectedCluster = Boolean(
    hasPlanTasks ||
      strategyTasksReady ||
      (isSelectedClusterFullyVisible && hasCoverageReady),
  );

  const shouldShowOnboardingInitialLoader = Boolean(
    !isInitialStrategyDataFetchPending &&
      !hasCompletedStrategyForSelectedCluster &&
      !isSelectedClusterGenerating &&
      !isRegenerateRunning &&
      isPersistedOnboardingStrategyLoading,
  );

  const shouldShowGenerateOverlay = Boolean(
    selectedClusterId &&
      !isInitialStrategyDataFetchPending &&
      !shouldShowOnboardingInitialLoader &&
      !isSelectedClusterStrategyLoading &&
      !isSelectedClusterFullyVisible &&
      !isSelectedClusterGenerating &&
      !isRegenerateRunning &&
      !hasPlanTasks &&
      !strategyTasksReady,
  );

  const shouldTreatPlanAsPending = Boolean(
    selectedClusterId &&
      hasGeneratedStrategyForSelectedCluster &&
      !hasPlanTasks &&
      !strategyTasksReady &&
      !isSelectedClusterFullyVisible &&
      (isSelectedClusterGenerating ||
        isRegenerateRunning ||
        isSelectedClusterStrategyLoading),
  );

  const shouldWaitForEvidence = Boolean(
    selectedClusterId &&
      hasGeneratedStrategyForSelectedCluster &&
      !hasPlanTasks &&
      !strategyTasksReady &&
      !isSelectedClusterFullyVisible &&
      hasCoverageReady &&
      !hasEvidenceData &&
      (isSelectedClusterGenerating ||
        isRegenerateRunning ||
        isSelectedClusterStrategyLoading ||
        !evidenceWaitExpired),
  );

  const isPlanLoading =
    (!hasPlanTasks &&
      !strategyTasksReady &&
      hasGeneratedStrategyForSelectedCluster &&
      hasCoverageSnapshot &&
      !hasCoverageReady) ||
    shouldWaitForEvidence ||
    shouldTreatPlanAsPending ||
    (!hasPlanTasks &&
      (regenerateStatus === "queued" ||
        regenerateStatus === "generating" ||
        planInitialLoading));

  const shouldShowInitialGenerationLoader = Boolean(
    !isInitialStrategyDataFetchPending && shouldShowOnboardingInitialLoader,
  );

  const shouldShowRefreshLoader = Boolean(
    selectedClusterId &&
      !shouldShowInitialGenerationLoader &&
      !shouldShowGenerateOverlay &&
      !isSelectedClusterFullyVisible &&
      isSelectedClusterStrategyLoading &&
      !hasGeneratedStrategyForSelectedCluster,
  );

  const shouldShowStrategyProcessingState = Boolean(
    selectedClusterId &&
      !shouldShowGenerateOverlay &&
      !shouldShowInitialGenerationLoader &&
      !shouldShowRefreshLoader &&
      !isSelectedClusterFullyVisible &&
      (isSelectedClusterStrategyLoading ||
        isSelectedClusterGenerating ||
        isRegenerateRunning ||
        isPlanLoading ||
        shouldWaitForEvidence ||
        shouldTreatPlanAsPending),
  );

  return {
    isSelectedClusterFullyVisible,
    hasGeneratedStrategyForSelectedCluster,
    hasCompletedStrategyForSelectedCluster,
    shouldShowOnboardingInitialLoader,
    shouldShowGenerateOverlay,
    isPlanLoading,
    shouldShowInitialGenerationLoader,
    shouldShowRefreshLoader,
    shouldShowStrategyProcessingState,
  };
}
